import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  type SxProps,
  type Theme,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { Z_INDEX } from '../../constants/zIndex';
import { responsiveFontSize, StackedActionButton } from '../../util/responsiveUtils';

const PAPER_MARGIN_MOBILE = 'max(12px, env(safe-area-inset-top, 0px)) 16px max(8px, env(safe-area-inset-bottom, 0px)) 16px';
const PAPER_MAX_HEIGHT_MOBILE = 'calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 24px - var(--keyboard-height, 0px))';
/** Full viewport height on mobile without subtracting keyboard (for long modals with inputs – they stay fullscreen and content scrolls). */
const PAPER_MAX_HEIGHT_MOBILE_FULLSCREEN = 'calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))';
const PAPER_MARGIN_MOBILE_FULLSCREEN = 'env(safe-area-inset-top, 0px) 0 env(safe-area-inset-bottom, 0px) 0';
const CONTENT_PB_MOBILE = 'max(16px, env(safe-area-inset-bottom, 0px))';

export interface AppDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Called when the dialog should close (e.g. backdrop click, close button) */
  onClose: () => void;
  /** Title content (string or node). Omit to hide the title bar. */
  title?: React.ReactNode;
  /** Optional action(s) in the title bar (e.g. custom buttons). Default close button is shown when showCloseButton is true. */
  titleAction?: React.ReactNode;
  /** Show close (X) button in title bar. Default true. */
  showCloseButton?: boolean;
  /** Main content */
  children: React.ReactNode;
  /** Footer actions (e.g. Cancel / Submit buttons). Omit for no footer. */
  actions?: React.ReactNode;
  /** Max width of the dialog. Default 'sm'. */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Make dialog full width. Default true on mobile. */
  fullWidth?: boolean;
  /** Whether content area scrolls when tall. Default true. */
  scrollableContent?: boolean;
  /** Custom max height on desktop (e.g. '90vh'). Mobile uses safe-area height. Default 'none' for small dialogs. */
  maxHeightDesktop?: string;
  /**
   * On mobile, render as fullscreen and do not shrink when the on-screen keyboard opens.
   * Use for longer modals that contain inputs: the modal stays full height and content scrolls
   * instead of the modal being squeezed or shifted up. Default false.
   */
  fullScreenOnMobile?: boolean;
  /**
   * When true and not fullscreen, on mobile the modal is positioned just above the on-screen
   * keyboard when it opens (so the modal is never covered). Use for non-fullscreen modals that
   * contain inputs. Default false.
   */
  positionAboveKeyboard?: boolean;
  /** Optional z-index for the dialog container */
  zIndex?: number;
  /** Disable closing on backdrop click */
  disableBackdropClick?: boolean;
  /** Disable closing on Escape key */
  disableEscapeKeyDown?: boolean;
  /** Optional divider below title. Default true when title is present. */
  divider?: boolean;
  /**
   * When true, removes the default spacer between the title bar and scrollable content
   * and is intended for dialogs that already use tight title/body padding (e.g. long lists).
   */
  compactTitleGap?: boolean;
  /** Sx overrides */
  titleSx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
  actionsSx?: SxProps<Theme>;
  paperSx?: SxProps<Theme>;
  /** Additional Paper props (merged with default safe-area styles) */
  PaperProps?: Partial<React.ComponentProps<typeof Dialog>['PaperProps']>;
  /** Additional Backdrop props */
  BackdropProps?: Partial<React.ComponentProps<typeof Dialog>['BackdropProps']>;
}

/**
 * App-wide dialog that respects safe areas (Dynamic Island, home indicator) and matches
 * the app design (ContentCard-style border, radius, backdrop). Use for all modals and popups.
 */
export const AppDialog: React.FC<AppDialogProps> = ({
  open,
  onClose,
  title,
  titleAction,
  showCloseButton = true,
  children,
  actions,
  maxWidth = 'sm',
  fullWidth = true,
  scrollableContent = true,
  maxHeightDesktop = 'none',
  fullScreenOnMobile = false,
  positionAboveKeyboard = false,
  zIndex = Z_INDEX.DIALOG,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  divider = true,
  compactTitleGap = false,
  titleSx,
  contentSx,
  actionsSx,
  paperSx,
  PaperProps: paperPropsOverride,
  BackdropProps: backdropPropsOverride,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleClose = (_event: object, reason: string) => {
    if (disableBackdropClick && reason === 'backdropClick') return;
    onClose();
  };

  const basePaperSx: SxProps<Theme> = {
    borderRadius: { xs: 2, sm: 3 },
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    backgroundImage: 'none',
    boxShadow:
      theme.palette.mode === 'dark'
        ? `0 24px 48px ${alpha(theme.palette.common.black, 0.5)}`
        : `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
    m: {
      xs: fullScreenOnMobile ? PAPER_MARGIN_MOBILE_FULLSCREEN : PAPER_MARGIN_MOBILE,
      sm: 3,
    },
    maxWidth: {
      xs: fullScreenOnMobile ? '100%' : 'calc(100% - 32px)',
      sm: maxWidth === 'xs' ? 444 : maxWidth === 'sm' ? 600 : maxWidth === 'md' ? 900 : 1200,
    },
    width: {
      xs: fullScreenOnMobile ? '100%' : 'calc(100% - 32px)',
      sm: fullWidth ? '100%' : 'auto',
    },
    /* When fullScreenOnMobile: on xs use full viewport height; on sm+ explicitly set auto so desktop never gets the mobile fullscreen height */
    ...(fullScreenOnMobile && {
      height: { xs: PAPER_MAX_HEIGHT_MOBILE_FULLSCREEN, sm: 'auto' },
    }),
    maxHeight: {
      xs: fullScreenOnMobile ? PAPER_MAX_HEIGHT_MOBILE_FULLSCREEN : PAPER_MAX_HEIGHT_MOBILE,
      sm: maxHeightDesktop,
    },
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    /* Smooth max-height adjustment when on-screen keyboard opens/closes (only when not fullScreenOnMobile) */
    transition: fullScreenOnMobile ? 'none' : 'max-height 0.25s ease-out',
  };

  const hasTitleBar = title != null || showCloseButton;

  /** Basis 0 so DialogContent gets a real max height inside flex Paper; `1 1 auto` often won't scroll on iOS WKWebView or Chrome mobile. */
  const dialogContentFlex =
    scrollableContent && (isMobile || maxHeightDesktop !== 'none') ? '1 1 0%' : '1 1 auto';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      disableEscapeKeyDown={disableEscapeKeyDown}
      scroll="paper"
      /* Mobile: never lock body — MUI scroll lock breaks touch/momentum scrolling inside the paper (Chrome + iOS app). Desktop keeps lock for layout stability. */
      disableScrollLock={isMobile}
      sx={{
        zIndex,
        '& .MuiDialog-container': {
          alignItems: isMobile && fullScreenOnMobile ? 'stretch' : 'center',
          ...(isMobile &&
            fullScreenOnMobile && {
              height: '100%',
              minHeight: '100dvh',
              maxHeight: '100dvh',
              boxSizing: 'border-box',
            }),
        },
      }}
      BackdropProps={{
        ...backdropPropsOverride,
        sx: {
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          ...(typeof backdropPropsOverride?.sx === 'object' && backdropPropsOverride?.sx),
        },
      }}
      PaperProps={{
        ...paperPropsOverride,
        className: [
          paperPropsOverride?.className,
          fullScreenOnMobile ? 'app-dialog-fullscreen-mobile' : null,
          positionAboveKeyboard && !fullScreenOnMobile ? 'modal-with-inputs' : null,
        ]
          .filter(Boolean)
          .join(' ') || undefined,
        sx: [basePaperSx, paperSx].filter(Boolean) as SxProps<Theme>,
      }}
    >
      {hasTitleBar && (
        <DialogTitle
          component="div"
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            py: { xs: 1.5, sm: 2 },
            px: { xs: 2, sm: 3 },
            borderBottom: divider ? `1px solid ${theme.palette.divider}` : 'none',
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            fontSize: responsiveFontSize.subtitle,
            fontWeight: 600,
            ...titleSx,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>{title}</Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {titleAction}
            {showCloseButton && (
              <IconButton
                onClick={onClose}
                aria-label="Close"
                size={isMobile ? 'medium' : 'small'}
                sx={{
                  color: theme.palette.text.secondary,
                  minWidth: StackedActionButton.MIN_HEIGHT,
                  minHeight: StackedActionButton.MIN_HEIGHT,
                  '@media (hover: hover)': {
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                      color: theme.palette.text.primary,
                    },
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
      )}

      {hasTitleBar && !compactTitleGap && (
        <Box
          aria-hidden
          sx={{
            flexShrink: 0,
            height: { xs: 2, sm: 2.5 },
            backgroundColor: theme.palette.background.paper,
          }}
        />
      )}

      <DialogContent
        sx={{
          flex: dialogContentFlex,
          minHeight: 0,
          overflowX: 'auto',
          overflowY: scrollableContent ? 'auto' : 'visible',
          px: { xs: 2, sm: 3 },
          py: hasTitleBar ? { xs: 2, sm: 2.5 } : { xs: 2, sm: 3 },
          pb: { xs: CONTENT_PB_MOBILE, sm: hasTitleBar ? 2.5 : 3 },
          backgroundColor: theme.palette.background.paper,
          ...(scrollableContent && {
            position: 'relative',
          }),
          ...(isMobile &&
            scrollableContent && {
              WebkitOverflowScrolling: 'touch',
              overscrollBehaviorY: 'contain',
              touchAction: 'pan-y',
            }),
          ...contentSx,
        }}
      >
        {children}
      </DialogContent>

      {actions != null && (
        <DialogActions
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
            pb: { xs: CONTENT_PB_MOBILE, sm: 2 },
            pt: 0,
            gap: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            ...actionsSx,
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};
